import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

/* DnD kit */
import {
    DndContext,
    DragOverlay,
    MeasuringStrategy,
} from '@dnd-kit/core';

import {
    SortableContext,
    verticalListSortingStrategy,
    horizontalListSortingStrategy
} from '@dnd-kit/sortable';

/* CSS */
import styles from './ItineraryBoard.module.css';

/* Hooks */
import { useDnd } from '../../hooks/useDnd';
import { useItineraryActions } from '../../hooks/useItineraryActions';
import { useReadOnlyToast } from '../../hooks/useReadOnlyToast';
import { useEditingBroadcast } from '../../hooks/useEditingBroadcast';
import { useIsTouch } from '../../hooks/useMediaQuery';

/* Components */
import { SortableDay } from './SortableDay';
import { SortableRow } from './SortableRow';
import { SortableHeader } from './SortableHeader';
import { PriceSplitCell } from './PriceSplitCell';

/* Context */
import { useTripContext } from './TripContext';
import { useAuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

/* Context Menu */
import ContextMenuManager from './ContextMenuManager';

/* Modals */
import ConfirmationModal from '../cards/ConfirmationModal';

/* Cell renderers */
import { TextCell, NumberCell, TimeCell, TagCell, UrlCell, FileCell, PersonCell } from '../../components/itinerary/CellRenderer';

/* Utils */
import { parsePrice } from '../../utils/statsUtils';
import { tripUtils } from '../../utils/tripUtils';
import { getColumnMinWidth } from '../../utils/columnUtils';

/* Icons */
import add_clicked from '../../assets/icons/add_clicked.svg';
import add_unclicked from '../../assets/icons/add_unclicked.svg';
import reorder_icon from '../../assets/icons/reorder_icon.svg';

/* ================== */
/* === COMPONENTS === */
/* ================== */

const CELL_TYPES = {
    'text': TextCell,
    'number': NumberCell,
    'time': TimeCell,
    'tags': TagCell,
    'url': UrlCell,
    'person': PersonCell,
    'files': FileCell,
    'price-split': PriceSplitCell,
};

const FIELD_OPTIONS = [
    { id: 'text', label: 'Text', defaultPlaceholder: 'Type anything...' },
    { id: 'time', label: 'Time', defaultPlaceholder: '' },
    { id: 'number', label: 'Number', defaultPlaceholder: '0' },
    { id: 'tags', label: 'Tags', defaultPlaceholder: '' },
    { id: 'url', label: 'URL', defaultPlaceholder: 'www.example.com' },
    { id: 'person', label: 'Person', defaultPlaceholder: 'E.g. me' },
    { id: 'files', label: 'Files & Media', defaultPlaceholder: '' },
    { id: 'price-split', label: 'Price Split', defaultPlaceholder: '' }
];

/* ============ */
/* === MAIN === */
/* ============ */

const ItineraryBoard = () => {

    const { id: tripId } = useParams();    // undefined in create mode → our autosave guard

    // Grab the data sets from our global context
    const {
        itineraryDays, setItineraryDays,
        tripMembers,
        setDefaultColumns,
        structureVersion, reloadTrip,
        canEdit,
        setIsDirty
    } = useTripContext();

    const { currentUser } = useAuthContext();

    // Available users (trip members or current user if no trip members)
    const availableUsers = tripMembers.length > 0
        ? tripMembers
        : (currentUser ? [{ id: currentUser.username, name: currentUser.username, isMe: true }] : []);

    // Itinerary table ref for scrolling
    const itineraryTableRef = useRef(null);

    // On touch devices the grid is always scroll-contained (resize is desktop-only)
    const isTouch = useIsTouch();

    // Column resizer
    const [resizeState, setResizeState] = useState(null);

    // Index of the day pending a delete-confirmation (null = no modal)
    const [dayToDelete, setDayToDelete] = useState(null);

    // Itinerary actions
    const itineraryActions = useItineraryActions(FIELD_OPTIONS);

    // Context menu
    const contextMenu = itineraryActions.contextMenu;
    const setContextMenu = itineraryActions.setContextMenu;

    // Toast for autosave debouncing
    const { showToast } = useToast();

    // Broadcasting
    const notifyReadOnly = useReadOnlyToast();
    const notifyEditing = useEditingBroadcast();

    const saveTimers = useRef({});   // rowId -> pending timeout (our debounce store)
    const layoutTimers = useRef({});
    const noteTimers = useRef({});
    const persistedLayouts = useRef({});   // dayId -> last-saved columns (serialized)

    // Keep the latest structure version in a ref so debounced saves read a fresh value
    const structureVersionRef = useRef(structureVersion);
    useEffect(() => { structureVersionRef.current = structureVersion; }, [structureVersion]);

    // On a 409 (item- or structure-conflict), refetch the latest and let the user know
    const handleSaveConflict = (err, label) => {
        if (err?.message?.includes('conflict')) {
            reloadTrip();
            showToast('This trip was updated elsewhere — refreshed to the latest.', 'info');
        } else if (err?.message?.includes('edit access')) {
            showToast('You have view-only access to this trip.', 'error');
        } else {
            showToast(label, 'error');
        }
    };

    // DnD hook
    const {
        sensors,
        customCollisionDetection,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        activeDragId,
        activeDragData
    } = useDnd(itineraryActions.initiateAction);

    // Pull the finger position out of the drag's original touch event (for menu placement)
    const getTouchPoint = (activatorEvent) => {
        const t = activatorEvent?.touches?.[0] || activatorEvent?.changedTouches?.[0];
        if (t) return { x: t.clientX, y: t.clientY };
        return { x: activatorEvent?.clientX || 0, y: activatorEvent?.clientY || 0 };
    };

    // On touch, a press-and-hold that ends WITHOUT reordering opens that row's context menu
    // The drag "lifts" the row and letting go without moving the menu (delete, insert, etc.).
    const handleBoardDragEnd = (event) => {
        handleDragEnd(event);   // run the normal reorder logic first

        if (!isTouch || !canEdit) return;
        const { active, over } = event;
        if (over && over.id !== active.id) return;          // a real reorder happened → no menu
        if (active?.data?.current?.type !== 'Row') return;  // columns open their menu on tap instead

        const rowId = active.data.current.rowId;
        const dayIndex = itineraryDays.findIndex(d => d.rows.some(r => r.id === rowId));
        if (dayIndex === -1) return;

        const { x, y } = getTouchPoint(event.activatorEvent);
        setContextMenu({ visible: true, x, y, type: 'row', dayIndex, targetId: rowId });
    };

    // Generate grid template string from column widths
    const getGridTemplate = (day) => day.columns
        .map((c, i) => {
            // Each track is clamped to the column's content minimum so a tag column
            // auto-widens the moment a tag is added and never renders too narrow to
            // reveal a pill's delete button (which would cause a hover-wrap flicker)
            const w = Math.max(c.width, getColumnMinWidth(c, day.rows));
            if (isTouch) return `minmax(${w}px, 1fr)`;
            if (i === day.columns.length - 1) return `minmax(${w}px, 1fr)`;
            return `${w}px`;
        })
        .join(' ');

    /* --------------------------------- */
    /* ---- ITINERARY TABLE ACTIONS ---- */
    /* --------------------------------- */

    // Handle day note updates
    const handleDayNoteChange = (dayIndex, value) => {
        if (!canEdit) return notifyReadOnly();
        setIsDirty(true);
        setItineraryDays(prev => prev.map((day, idx) => {
            if (idx !== dayIndex) return day;
            return { ...day, note: value };
        }));

        if (tripId) {
            const dayId = itineraryDays[dayIndex].id;
            clearTimeout(noteTimers.current[dayId]);
            noteTimers.current[dayId] = setTimeout(() => {
                tripUtils.updateDay(tripId, dayId, { note: value })
                    .catch(() => showToast('Failed to save note', 'error'));
            }, 600);
        }

    };

    // Handle column resize
    const handleResize = (e, dayIndex, colId, currentWidth) => {
        if (!canEdit) return notifyReadOnly();
        e.preventDefault(); // Prevent text selection

        const day = itineraryDays[dayIndex];
        const col = day.columns.find(c => c.id === colId);
        const minWidth = getColumnMinWidth(col, day.rows);

        setResizeState({
            colId,
            startX: e.clientX,
            startWidth: Math.max(currentWidth, minWidth),
            minWidth,
            dayIndex
        });
        document.body.classList.add('is-resizing');
        setIsDirty(true);
    };

    // Itinerary row update after the field value has been changed
    const handleItineraryFieldChange = (dayIndex, rowId, field, value) => {
        if (!canEdit) return notifyReadOnly();
        if (tripId) notifyEditing();
        setIsDirty(true);
        setItineraryDays((prev) =>
            prev.map((day, dIdx) => {
                // Filter to the correct day
                if (dIdx !== dayIndex) return day;
                // Filter to the correct row
                return {
                    ...day,
                    rows: day.rows.map((row) =>
                        row.id === rowId
                            ? { ...row, [field]: value }
                            : row
                    )
                };
            })
        );

        // ---- Autosave (edit mode only): debounced PATCH of just this row ----
        if (!tripId) return;

        const day = itineraryDays[dayIndex];
        const dayId = day.id;
        const updatedRow = { ...day.rows.find(r => r.id === rowId), [field]: value };

        clearTimeout(saveTimers.current[rowId]);         // cancel this row's previous pending save
        saveTimers.current[rowId] = setTimeout(() => {   // fire 600ms after typing stops
            tripUtils.updateItem(tripId, rowId, updatedRow, updatedRow._version, structureVersionRef.current)
                .then(res => {
                    // Record the bumped version on the row so the next edit isn't stale
                    if (res?.version != null) {
                        setItineraryDays(prev => prev.map(d => d.id !== dayId ? d : {
                            ...d,
                            rows: d.rows.map(r => r.id === rowId ? { ...r, _version: res.version } : r)
                        }));
                    }
                })
                .catch(err => handleSaveConflict(err, 'Failed to save changes'));
            delete saveTimers.current[rowId];
        }, 600);
    }

    const handleAddItinerayRow = (dayIndex) => {
        itineraryActions.executeRowAction('ADD_ROW', { dayIndex });
    }

    const handleRemoveItineraryRow = (dayIndex, rowId) => {
        itineraryActions.executeRowAction('DELETE_ROW', { dayIndex, targetId: rowId });
    }

    const handleRemoveColumn = (dayIndex, colId) => {
        itineraryActions.initiateAction('DELETE_COLUMN', { dayIndex, targetId: colId });
    };

    // A day is "empty" when no row has any non-blank value in any column
    const isDayEmpty = (day) => !day.rows.some(r => day.columns.some(col => {
        const val = r[col.id];
        return val && (Array.isArray(val) ? val.length > 0 : val.toString().trim() !== '');
    }));

    const handleDeleteDay = (dayIndex) => {
        // Empty day (or the last-day guard case) → delete straight away.
        // Day with content → confirm first so the user doesn't lose entries.
        if (isDayEmpty(itineraryDays[dayIndex]) || itineraryDays.length <= 1) {
            itineraryActions.deleteDay(dayIndex);
            return;
        }
        setDayToDelete(dayIndex);
    };

    const handleConfirmDeleteDay = () => {
        if (dayToDelete !== null) itineraryActions.deleteDay(dayToDelete);
        setDayToDelete(null);
    };

    const handleAddDay = () => {
        itineraryActions.addDay();
    };

    /* ------------ */
    /* --- TAGS --- */
    /* ------------ */

    // Handle tag updates for a specific column (syncs across all days)
    const handleColumnTagsUpdate = (colId, newTags) => {
        setIsDirty(true);

        // Update default columns (so when user create new days, it will inherit it)
        setDefaultColumns(prev => prev.map(col =>
            col.id === colId ? { ...col, tags: newTags } : col
        ));

        // Update existing days
        setItineraryDays(prev => prev.map(day => ({
            ...day,
            columns: day.columns.map(col =>
                col.id === colId ? { ...col, tags: newTags } : col
            )
        })));
    };

    // Handle specific tag renames to update existing rows with the new value
    const handleColumnTagRename = (colId, oldTag, newTag) => {
        if (!oldTag || !newTag || oldTag === newTag) return;
        setIsDirty(true);

        const cleanName = newTag.trim();

        // Update default columns
        setDefaultColumns(prev => prev.map(col =>
            col.id === colId && col.tags
                ? { ...col, tags: col.tags.map(t => t === oldTag ? cleanName : t) }
                : col
        ));

        setItineraryDays(prev => prev.map(day => ({
            ...day,
            // Update the options for the dropdown
            columns: day.columns.map(col =>
                col.id === colId && col.tags
                    ? { ...col, tags: col.tags.map(t => t === oldTag ? cleanName : t) }
                    : col
            ),
            // Update all selected values in the rows
            rows: day.rows.map(row => {
                const currentVal = row[colId] || [];

                // The value is an array of strings (multiple tags selected)
                if (Array.isArray(currentVal) && currentVal.includes(oldTag)) {
                    return {
                        ...row,
                        [colId]: currentVal.map(t => t === oldTag ? cleanName : t)
                    };
                }

                // Default: No match, return row untouched
                return row;
            })
        })));
    };

    // Handle deleting tags globally from both options and existing rows
    const handleColumnTagDelete = (colId, deletedTag) => {
        if (!deletedTag) return;
        setIsDirty(true);

        // Remove from default columns so new days don't get it
        setDefaultColumns(prev => prev.map(col =>
            col.id === colId && col.tags
                ? { ...col, tags: col.tags.filter(t => t !== deletedTag) }
                : col
        ));

        setItineraryDays(prev => prev.map(day => ({
            ...day,
            // Remove from the available dropdown options list
            columns: day.columns.map(col =>
                col.id === colId && col.tags
                    ? { ...col, tags: col.tags.filter(t => t !== deletedTag) }
                    : col
            ),
            // Erase the tag from any activity row that currently uses it
            rows: day.rows.map(row => {
                const currentVal = row[colId] || [];

                if (Array.isArray(currentVal) && currentVal.includes(deletedTag)) {
                    return {
                        ...row,
                        [colId]: currentVal.filter(t => t !== deletedTag)
                    };
                }

                // Default: Return row untouched
                return row;
            })
        })));
    };

    /* ----------------- */
    /* --- LISTENERS --- */
    /* ----------------- */

    // Listen to menu dismissal
    useEffect(() => {
        if (!contextMenu) return;

        const handleDismiss = () => setContextMenu(null);
        const handleScrollDismiss = () => setContextMenu(null);

        document.addEventListener('click', handleDismiss);
        window.addEventListener('scroll', handleScrollDismiss, true);

        return () => {
            document.removeEventListener('click', handleDismiss);
            window.removeEventListener('scroll', handleScrollDismiss, true);

            document.body.style.overflow = ''; // Cleanup just in case
        };
    }, [contextMenu]);

    // Listen to mouse move and mouse up events for column resizing
    useEffect(() => {
        if (!resizeState) return;

        const handleMouseMove = (e) => {
            const deltaX = e.clientX - resizeState.startX;

            /* Never shrink below the column's content-aware minimum */
            const newWidth = Math.max(resizeState.startWidth + deltaX, resizeState.minWidth);

            setItineraryDays(prev => prev.map((day, idx) => {
                if (idx !== resizeState.dayIndex) return day;

                return {
                    ...day,
                    columns: day.columns.map(c =>
                        c.id === resizeState.colId
                            ? { ...c, width: newWidth }
                            : c
                    )
                };
            }));
        };

        const handleMouseUp = () => {
            setResizeState(null);
            document.body.classList.remove('is-resizing');
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('is-resizing'); // Cleanup in case
        };
    }, [resizeState]);

    // Listen to layout changes
    useEffect(() => {
        if (!tripId) return;
        itineraryDays.forEach(day => {
            const serialized = JSON.stringify(day.columns);
            const prev = persistedLayouts.current[day.id];
            persistedLayouts.current[day.id] = serialized;

            // A dedicated column endpoint (e.g. delete) already saved this day's layout → skip one sync.
            if (itineraryActions.columnSyncSkip.current.has(day.id)) {
                itineraryActions.columnSyncSkip.current.delete(day.id);
                return;
            }

            if (prev === undefined || prev === serialized) return;   // first sight (load) or unchanged → skip
            clearTimeout(layoutTimers.current[day.id]);
            layoutTimers.current[day.id] = setTimeout(() => {
                tripUtils.updateDay(tripId, day.id, { layout: day.columns })
                    .catch(() => showToast('Failed to save columns', 'error'));
            }, 500);
        });
    }, [itineraryDays, tripId]);

    return (
        <section>
            <DndContext
                sensors={sensors}
                collisionDetection={customCollisionDetection}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleBoardDragEnd}
                measuring={{
                    droppable: {
                        // Measure droppables only while dragging (not on every re-render)
                        strategy: MeasuringStrategy.WhileDragging,
                    },
                }}
            >
                <section className={styles.newTripItinerary}>
                    <SortableContext
                        items={itineraryDays.map(d => d.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {itineraryDays.map((day, dayIndex) => {

                            {/* Check if the entire day has no data in any row */ }
                            const isDayDataEmpty = isDayEmpty(day);

                            return (
                                <SortableDay key={day.id} id={day.id} disabled={false}>
                                    {({ listeners, attributes }) => (

                                        /* Each itinerary day */
                                        <div className={styles.itineraryDay}>

                                            {/* Day header */}
                                            <div className={styles.itineraryDayHeader}>
                                                <div className={`${styles.dayTitleWrapper} ${!canEdit ? styles.dayTitleWrapperReadOnly : ''}`}>
                                                    {/* Drag Handle */}
                                                    {canEdit && !isTouch && (
                                                        <div className={styles.dayDragHandle}>
                                                            <img
                                                                src={reorder_icon}
                                                                alt="Drag Day"
                                                                className={styles.dayDragIcon}
                                                                {...listeners}
                                                                {...attributes}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Delete day button */}
                                                    {canEdit && (
                                                        <button
                                                            type='button'
                                                            className={styles.itineraryDayDelete}
                                                            onClick={() => handleDeleteDay(dayIndex)}
                                                            title="Delete day"
                                                        >
                                                            ×
                                                        </button>
                                                    )}

                                                    <span
                                                        className={`${styles.itineraryDayTitle}${isTouch ? ` ${styles.dayTitleDraggable}` : ''}`}
                                                        {...(isTouch ? listeners : {})}
                                                        {...(isTouch ? attributes : {})}
                                                    >
                                                        Day {dayIndex + 1}
                                                    </span>
                                                </div>

                                                <div className={styles.itineraryDayHeaderRow}>
                                                    <span className={styles.itineraryDayDate}>
                                                        <b>Date:</b> &nbsp;
                                                        {day.date.toLocaleDateString('en-AU', {
                                                            weekday: 'short',
                                                            day: '2-digit',
                                                            month: 'short',
                                                        })}
                                                    </span>

                                                    <span className={styles.itineraryDayNote}>
                                                        <b>Note:</b> &nbsp;
                                                        <input
                                                            type="text"
                                                            className={styles.itineraryDayNoteInput}
                                                            placeholder="Add notes for this day..."
                                                            value={day.note || ''}
                                                            onChange={(e) => handleDayNoteChange(dayIndex, e.target.value)}
                                                        />
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Itinerary wrapper */}
                                            <div className={styles.itineraryTableWrapper}>

                                                {/* Itinerary rows + add column button */}
                                                <div
                                                    className={styles.itineraryTable}
                                                    ref={itineraryTableRef}
                                                    style={{
                                                        overflowX: (isTouch || day.columns.reduce((sum, col) => sum + col.width, 0) > 1040) ? 'auto' : 'visible',
                                                        maxWidth: (isTouch || day.columns.reduce((sum, col) => sum + col.width, 0) > 1040) ? '100%' : 'none'
                                                    }}
                                                >

                                                    {/* Table header */}
                                                    <div
                                                        className={styles.itineraryTableHeader}
                                                        style={{ gridTemplateColumns: getGridTemplate(day) }}
                                                    >
                                                        <SortableContext
                                                            id={day.id}
                                                            items={day.columns.map(c => `${day.id}:${c.id}`)}
                                                            strategy={horizontalListSortingStrategy}
                                                        >
                                                            {/* Column labels */}
                                                            {day.columns.map((col) => (
                                                                <SortableHeader
                                                                    key={`${day.id}:${col.id}`}
                                                                    id={`${day.id}:${col.id}`}
                                                                    disabled={false}
                                                                >
                                                                    {({ listeners, attributes }) => (
                                                                        <div
                                                                            className={styles.headerCell}
                                                                            onContextMenu={(e) => {
                                                                                // On touch the column menu opens on tap (onClick below); suppress
                                                                                // the browser's native long-press menu so it doesn't clash with drag.
                                                                                if (isTouch) { e.preventDefault(); return; }
                                                                                itineraryActions.handleColumnContextMenu(e, dayIndex, col.id, col.label);
                                                                            }}
                                                                            onClick={(e) => itineraryActions.handleColumnContextMenu(e, dayIndex, col.id, col.label)}
                                                                        >
                                                                            {/* Drag handle */}
                                                                            {!isTouch && (
                                                                                <div className={styles.columnDragHandle}>
                                                                                    <img
                                                                                        src={reorder_icon}
                                                                                        alt="Drag column"
                                                                                        className={styles.columnDragIcon}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        {...listeners}
                                                                                        {...attributes}
                                                                                    />
                                                                                </div>
                                                                            )}

                                                                            {/* Column label */}
                                                                            <span title={col.label}>{col.label}</span>

                                                                            {/* Delete column button */}
                                                                            {canEdit && (
                                                                                <button
                                                                                    type='button'
                                                                                    className={styles.itineraryColumnDelete}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleRemoveColumn(dayIndex, col.id);
                                                                                    }}
                                                                                    title={`Delete column "${col.label}"`}
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            )}

                                                                            {/* Resize handle */}
                                                                            <div
                                                                                className={styles.resizer}
                                                                                onMouseDown={(e) => handleResize(e, dayIndex, col.id, col.width)}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </SortableHeader>
                                                            ))}
                                                        </SortableContext>
                                                    </div>

                                                    {/* Itinerary rows */}
                                                    <SortableContext
                                                        id={day.id}
                                                        items={day.rows.map(r => r.id)}
                                                        strategy={verticalListSortingStrategy}
                                                    >
                                                        {day.rows.map((row, rowIndex) => {

                                                            const isGuideRow = isDayDataEmpty && rowIndex === 0;

                                                            // A PriceSplitCell pulls its amount from the row's Price (number) column when one exists
                                                            const priceCol = day.columns.find(c => c.id === 'price' && c.type === 'number')
                                                                || day.columns.find(c => c.type === 'number');
                                                            const linkedPrice = priceCol ? parsePrice(row[priceCol.id]) : null;

                                                            return (
                                                                <SortableRow key={row.id} id={row.id} disabled={false}>
                                                                    {({ listeners, attributes }) => (
                                                                        <div
                                                                            className={styles.itineraryRow}
                                                                            style={{ gridTemplateColumns: getGridTemplate(day) }}
                                                                            onContextMenu={(e) => {
                                                                                // On touch the row menu opens via long-press-release (see handleBoardDragEnd),
                                                                                // so suppress the browser's native long-press menu here.
                                                                                if (isTouch) { e.preventDefault(); return; }
                                                                                itineraryActions.handleRowContextMenu(e, dayIndex, row.id);
                                                                            }}
                                                                        >
                                                                            {/* Drag handle */}
                                                                            {!isTouch && (
                                                                                <div className={styles.rowDragHandle}>
                                                                                    <img
                                                                                        src={reorder_icon}
                                                                                        alt="Drag row"
                                                                                        className={styles.rowDragIcon}
                                                                                        {...listeners}
                                                                                        {...attributes}
                                                                                    />
                                                                                </div>
                                                                            )}

                                                                            {/* Delete row button */}
                                                                            {canEdit && (
                                                                                <button
                                                                                    type='button'
                                                                                    className={styles.itineraryRowDelete}
                                                                                    onClick={() => handleRemoveItineraryRow(dayIndex, row.id)}
                                                                                    title="Delete row"
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            )}

                                                                            {/* Itinerary row inputs */}
                                                                            {day.columns.map((col) => {

                                                                                const CellComponent = CELL_TYPES[col.type] || TextCell;

                                                                                return (
                                                                                    <div
                                                                                        key={col.id}
                                                                                        className={styles.itineraryCellWrapper}
                                                                                        data-sync-col={`${day.id}:${col.id}`}
                                                                                    >
                                                                                        <CellComponent
                                                                                            value={row[col.id]}
                                                                                            onChange={(val) =>
                                                                                                handleItineraryFieldChange(
                                                                                                    dayIndex,
                                                                                                    row.id,
                                                                                                    col.id,
                                                                                                    val
                                                                                                )
                                                                                            }
                                                                                            placeholder={isGuideRow ? col.placeholder : ''}
                                                                                            isGuideMode={isGuideRow}
                                                                                            onContextMenu={(e) => itineraryActions.handleRowContextMenu(e, dayIndex, row.id)}
                                                                                            options={col.tags || []}
                                                                                            onOptionsChange={(newTags) => handleColumnTagsUpdate(col.id, newTags)}
                                                                                            onTagRename={(oldTag, newTag) => handleColumnTagRename(col.id, oldTag, newTag)}
                                                                                            onTagDelete={(deletedTag) => handleColumnTagDelete(col.id, deletedTag)}
                                                                                            availableUsers={availableUsers}
                                                                                            isCostCategory={col.id === 'category'}
                                                                                            linkedPrice={linkedPrice}
                                                                                            onLinkedPriceChange={priceCol ? (newAmount) => handleItineraryFieldChange(dayIndex, row.id, priceCol.id, newAmount) : undefined}
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </SortableRow>
                                                            );
                                                        })}
                                                    </SortableContext>
                                                </div>

                                                {/* Add column button */}
                                                {canEdit && (
                                                    <button
                                                        type='button'
                                                        className={styles.itineraryTableAddColumn}
                                                        onClick={() => itineraryActions.initiateAction('ADD_COLUMN', { dayIndex })}
                                                        title="Add new column"
                                                    >
                                                        <img
                                                            src={add_unclicked}
                                                            alt="Add new column"
                                                            className={styles.itineraryTableAddIcon}
                                                            onMouseEnter={e => e.currentTarget.src = add_clicked}
                                                            onMouseLeave={e => e.currentTarget.src = add_unclicked}
                                                        />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Add row button */}
                                            {canEdit && (
                                                <button
                                                    type='button'
                                                    className={styles.itineraryAddRow}
                                                    onClick={() => handleAddItinerayRow(dayIndex)}
                                                >
                                                    + Add new row
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </SortableDay>
                            );
                        })}
                    </SortableContext>

                    {/* Add a new day button */}
                    {canEdit && (
                        <button
                            type='button'
                            className={styles.addDayButton}
                            onClick={handleAddDay}
                        >
                            + Add a new day
                        </button>
                    )}
                </section>

                {/* Global Drag Overlay */}
                <DragOverlay>
                    {activeDragData?.type === 'Column' ? (() => {

                        let dayId = activeDragData.sortable?.containerId;
                        // Extract real ID if composite (e.g., "day1:time" -> "time")
                        const safeActiveDragId = String(activeDragId || '');
                        const realColId = safeActiveDragId.includes(':') ? safeActiveDragId.split(':').pop() : activeDragId;

                        let day = itineraryDays.find(d => d.id === dayId);
                        if (!day && safeActiveDragId.includes(':')) {
                            const potentialDayId = safeActiveDragId.split(':')[0];
                            day = itineraryDays.find(d => d.id === potentialDayId);
                        }

                        const col = day?.columns.find(c => c.id === realColId);
                        if (!day || !col) return null;

                        return (
                            <div className={styles.dragOverlayColumn}>
                                {/* Ghost Header */}
                                <div className={styles.dragOverlayColumnHeader} style={{ width: `${col.width}px` }}>
                                    {col.label}
                                </div>

                                {/* Ghost Cells */}
                                <div className={styles.dragOverlayColumnCells}>
                                    {day.rows.slice(0, 3).map(row => {
                                        const CellComponent = CELL_TYPES[col.type] || CELL_TYPES.text;
                                        return (
                                            <div key={row.id} className={styles.dragOverlayColumnCell} style={{ width: `${col.width}px` }}>
                                                <div className={styles.itineraryCellWrapper}>
                                                    <CellComponent
                                                        value={row[col.id]}
                                                        onChange={() => { }}
                                                        options={col.tags || []}
                                                        availableUsers={availableUsers}
                                                        placeholder={col.placeholder || ''}
                                                        isGuideMode={false}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Visual truncated check */}
                                {day.rows.length > 3 && (
                                    <div className={styles.dragOverlayTruncation}>
                                        ...
                                    </div>
                                )}
                            </div>
                        );
                    })() : null}

                    {activeDragData?.type === 'Day' ? (() => {
                        const dayId = activeDragId;
                        const dayIndex = itineraryDays.findIndex(d => d.id === dayId);
                        const day = itineraryDays[dayIndex];

                        if (!day) return null;

                        return (
                            <div className={styles.dragOverlayDay}>
                                {/* Ghost Day */}
                                <div className={styles.itineraryDay}>
                                    <div className={styles.itineraryDayHeader}>
                                        <div className={styles.dayTitleWrapper}>
                                            <div className={styles.dayDragHandle}>
                                                <img src={reorder_icon} alt="Drag Day" className={styles.dayDragIcon} />
                                            </div>
                                            <span className={styles.itineraryDayTitle}>
                                                Day {dayIndex + 1}
                                            </span>
                                        </div>
                                        <div className={styles.itineraryDayHeaderRow}>
                                            <span className={styles.itineraryDayDate}>
                                                <b>Date:</b> &nbsp;
                                                {day.date.toLocaleDateString('en-AU', {
                                                    weekday: 'short',
                                                    day: '2-digit',
                                                    month: 'short',
                                                })}
                                            </span>
                                            <span className={styles.itineraryDayNote}>
                                                <b>Note:</b> &nbsp;
                                                <input
                                                    type="text"
                                                    className={styles.itineraryDayNoteInput}
                                                    placeholder="Add notes for this day..."
                                                    value={day.note || ''}
                                                    readOnly
                                                />
                                            </span>
                                        </div>
                                    </div>

                                    <div className={styles.itineraryTableWrapper}>
                                        <div className={styles.dragOverlayDayContent}>
                                            Moving {day.rows.length} row{day.rows.length !== 1 ? 's' : ''}...
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })() : null}
                </DragOverlay>
            </DndContext >

            <ContextMenuManager
                itineraryActions={itineraryActions}
                FIELD_OPTIONS={FIELD_OPTIONS}
            />

            {/* Confirm before deleting a day that still has content */}
            <ConfirmationModal
                isOpen={dayToDelete !== null}
                title="Delete this day?"
                message={dayToDelete !== null
                    ? `Day ${dayToDelete + 1} has activities in it. Deleting it will remove those entries and cannot be undone.`
                    : ''}
                onClose={() => setDayToDelete(null)}
                onConfirm={handleConfirmDeleteDay}
                confirmText="Delete"
                cancelText="Cancel"
                isDanger={true}
                showCancel={true}
            />
        </section>
    );
};

export default ItineraryBoard;
